"use client";
import { useState, useEffect, useMemo, Fragment } from "react";
import axios from "axios";
import AccountingReports3 from "./AccountingReports3";

const API = "https://erp-xe9u.onrender.com/api";

// ─── Formatting ─────────────────────────────────────────────────────────────
const fmt = (n) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return v < 0 ? `-$${s}` : `$${s}`;
};
const fmtCompact = (n) => {
  const v = Number(n || 0);
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(a >= 1e5 ? 0 : 1)}k`;
  return `${sign}$${a.toFixed(0)}`;
};
const fmtPct = (n) =>
  n == null || !isFinite(n) ? "—" : `${Number(n).toFixed(1)}%`;
const fmtNum = (n, d = 0) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: d });
const fmtDay = (d) =>
  d.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
const cls = (...c) => c.filter(Boolean).join(" ");
const pad = (n) => String(n).padStart(2, "0");
const toISODate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// ─── Range handling ─────────────────────────────────────────────────────────
const NOW = new Date();
const CUR_Q = Math.floor(NOW.getMonth() / 3) + 1;
const YEARS = Array.from({ length: 6 }, (_, i) => NOW.getFullYear() - i);

const INITIAL_RANGE = {
  mode: "quarter",
  year: NOW.getFullYear(),
  quarter: CUR_Q,
  month: `${NOW.getFullYear()}-${pad(NOW.getMonth() + 1)}`,
  from: toISODate(new Date(NOW.getFullYear(), 0, 1)),
  to: toISODate(NOW),
};

function resolveRange(r) {
  if (r.mode === "quarter") {
    const s = new Date(r.year, (r.quarter - 1) * 3, 1);
    const e = new Date(r.year, r.quarter * 3, 0, 23, 59, 59, 999);
    return { start: s, end: e, label: `Q${r.quarter} ${r.year}` };
  }
  if (r.mode === "month") {
    const [y, m] = String(r.month).split("-").map(Number);
    if (!y || !m) return null;
    const s = new Date(y, m - 1, 1);
    const e = new Date(y, m, 0, 23, 59, 59, 999);
    return {
      start: s,
      end: e,
      label: s.toLocaleString("en-CA", { month: "long", year: "numeric" }),
    };
  }
  if (r.mode === "year") {
    const s = new Date(r.year, 0, 1);
    const e = new Date(r.year, 11, 31, 23, 59, 59, 999);
    return { start: s, end: e, label: String(r.year) };
  }
  const [fy, fm, fd] = String(r.from).split("-").map(Number);
  const [ty, tm, td] = String(r.to).split("-").map(Number);
  if (!fy || !ty) return null;
  const s = new Date(fy, fm - 1, fd);
  const e = new Date(ty, tm - 1, td, 23, 59, 59, 999);
  if (s > e) return null;
  return { start: s, end: e, label: `${fmtDay(s)} – ${fmtDay(e)}` };
}

const PRESETS = [
  {
    label: "This quarter",
    apply: (r) => ({
      ...r,
      mode: "quarter",
      year: NOW.getFullYear(),
      quarter: CUR_Q,
    }),
  },
  {
    label: "Last quarter",
    apply: (r) =>
      CUR_Q === 1
        ? { ...r, mode: "quarter", year: NOW.getFullYear() - 1, quarter: 4 }
        : {
            ...r,
            mode: "quarter",
            year: NOW.getFullYear(),
            quarter: CUR_Q - 1,
          },
  },
  {
    label: "This month",
    apply: (r) => ({
      ...r,
      mode: "month",
      month: `${NOW.getFullYear()}-${pad(NOW.getMonth() + 1)}`,
    }),
  },
  {
    label: "Last month",
    apply: (r) => {
      const d = new Date(NOW.getFullYear(), NOW.getMonth() - 1, 1);
      return {
        ...r,
        mode: "month",
        month: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`,
      };
    },
  },
  {
    label: "Year to date",
    apply: (r) => ({
      ...r,
      mode: "custom",
      from: toISODate(new Date(NOW.getFullYear(), 0, 1)),
      to: toISODate(NOW),
    }),
  },
  {
    label: "Last 12 months",
    apply: (r) => ({
      ...r,
      mode: "custom",
      from: toISODate(new Date(NOW.getFullYear(), NOW.getMonth() - 11, 1)),
      to: toISODate(NOW),
    }),
  },
];

// ─── Series definitions ─────────────────────────────────────────────────────
const COST_SERIES = [
  {
    label: "Materials",
    get: (m) => m.matDelivered + m.matSurcharge,
    color: "bg-orange-400",
  },
  { label: "Piecework", get: (m) => m.pwApproved, color: "bg-purple-400" },
  { label: "Hourly", get: (m) => m.tsApproved, color: "bg-sky-400" },
  { label: "Subcontractors", get: (m) => m.subProcessed, color: "bg-teal-400" },
  { label: "Sub agreements", get: (m) => m.saActual, color: "bg-rose-400" },
  { label: "Payment fees", get: (m) => m.payFees, color: "bg-amber-300" },
];
const BILL_SERIES = [
  {
    label: "Progress draws",
    get: (m) => m.invProgressNet,
    color: "bg-emerald-500",
  },
  {
    label: "CO invoices",
    get: (m) => m.invStandaloneNet,
    color: "bg-emerald-300",
  },
  {
    label: "Holdback released",
    get: (m) => m.invHoldbackRelease,
    color: "bg-lime-300",
  },
];

// ─── Table column sets ──────────────────────────────────────────────────────
const marginTone = (r) =>
  r.invoiced <= 0 && r.costIncurred <= 0
    ? "text-gray-500"
    : r.margin < 0
      ? "text-red-400"
      : "text-green-400";

const COLUMNS = {
  overview: [
    {
      key: "coValue",
      label: "COs approved",
      get: (r) => r.coValue,
      sub: (r) => (r.coCount ? `${r.coCount} approved` : null),
      tone: () => "text-purple-300",
    },
    { key: "costIncurred", label: "Cost incurred", get: (r) => r.costIncurred },
    {
      key: "committed",
      label: "Committed",
      get: (r) => r.committed,
      tone: () => "text-yellow-400",
    },
    {
      key: "invoiced",
      label: "Invoiced",
      get: (r) => r.invoiced,
      tone: () => "text-green-400",
    },
    { key: "margin", label: "Margin", get: (r) => r.margin, tone: marginTone },
    {
      key: "marginPct",
      label: "Margin %",
      get: (r) => r.marginPct,
      kind: "pct",
      tone: marginTone,
    },
    {
      key: "billedToDatePct",
      label: "Billed to date",
      get: (r) => r.billedToDatePct,
      kind: "pct",
      sub: (r) =>
        r.contractValue ? `of ${fmtCompact(r.contractValue)}` : null,
    },
  ],
  costs: [
    {
      key: "matDelivered",
      label: "Materials delivered",
      get: (r) => r.matDelivered,
      tone: () => "text-orange-300",
    },
    { key: "matSurcharge", label: "Surcharges", get: (r) => r.matSurcharge },
    {
      key: "pwApproved",
      label: "Piecework",
      get: (r) => r.pwApproved,
      sub: (r) => (r.pwLogs ? `${r.pwLogs} logs` : null),
      tone: () => "text-purple-300",
    },
    {
      key: "tsApproved",
      label: "Hourly",
      get: (r) => r.tsApproved,
      sub: (r) => (r.tsHours ? `${fmtNum(r.tsHours, 1)} h` : null),
      tone: () => "text-sky-300",
    },
    {
      key: "subProcessed",
      label: "Subcontractors",
      get: (r) => r.subProcessed,
      sub: (r) => (r.subBills ? `${r.subBills} bills` : null),
      tone: () => "text-teal-300",
    },
    {
      key: "saActual",
      label: "Sub agreements",
      get: (r) => r.saActual,
      sub: (r) => (r.saCount ? `${r.saCount} completed` : null),
      tone: () => "text-rose-300",
    },
    {
      key: "payFees",
      label: "Payment fees",
      get: (r) => r.payFees,
      tone: () => "text-amber-300",
    },
    { key: "labourBurden", label: "Labour burden", get: (r) => r.labourBurden },
    {
      key: "committed",
      label: "Committed",
      get: (r) => r.committed,
      tone: () => "text-yellow-400",
    },
    { key: "costIncurred", label: "Cost incurred", get: (r) => r.costIncurred },
  ],
  billing: [
    {
      key: "invProgressNet",
      label: "Progress draws",
      get: (r) => r.invProgressNet,
    },
    {
      key: "invStandaloneNet",
      label: "CO invoices",
      get: (r) => r.invStandaloneNet,
    },
    {
      key: "invHoldbackRelease",
      label: "Holdback released",
      get: (r) => r.invHoldbackRelease,
    },
    {
      key: "holdbackDeducted",
      label: "Holdback retained",
      get: (r) => r.holdbackDeducted + r.standaloneHoldback,
      tone: () => "text-purple-300",
    },
    {
      key: "invoiced",
      label: "Invoiced (net)",
      get: (r) => r.invoiced,
      tone: () => "text-green-400",
    },
    {
      key: "paid",
      label: "Paid",
      get: (r) => r.paid,
      tone: () => "text-blue-400",
    },
    {
      key: "ar",
      label: "Receivable",
      get: (r) => r.ar,
      tone: (r) => (r.ar > 0 ? "text-orange-300" : "text-gray-500"),
    },
  ],
};

const VIEWS = [
  { key: "overview", label: "Overview" },
  { key: "costs", label: "Costs" },
  { key: "billing", label: "Billing" },
];

// ════════════════════════════════════════════════════════════════════════════
// Small pieces
// ════════════════════════════════════════════════════════════════════════════
function Segmented({ options, value, onChange, size = "md" }) {
  return (
    <div className="inline-flex bg-gray-900 border border-gray-700 rounded-lg p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cls(
            "rounded-md font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400",
            size === "sm" ? "px-2.5 py-1 text-xs" : "px-3.5 py-1.5 text-sm",
            value === o.key
              ? "bg-orange-500 text-white"
              : "text-gray-400 hover:text-white",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Figure({ label, value, sub, tone = "text-white" }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-400">{label}</div>
      <div
        className={cls(
          "text-2xl md:text-3xl font-semibold tabular-nums tracking-tight mt-1",
          tone,
        )}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-gray-500 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function Legend({ series }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {series.map((s) => (
        <span
          key={s.label}
          className="flex items-center gap-1.5 text-[11px] text-gray-400"
        >
          <span className={cls("w-2.5 h-2.5 rounded-sm", s.color)} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function StackBar({ series, m, max, title }) {
  const parts = series.map((s) => ({ ...s, v: Math.max(0, s.get(m) || 0) }));
  const total = parts.reduce((a, p) => a + p.v, 0);
  return (
    <div
      className="w-3 sm:w-4 h-full flex items-end"
      title={`${title}: ${fmt(total)}`}
    >
      <div
        className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden"
        style={{
          height: `${(total / max) * 100}%`,
          minHeight: total > 0 ? 2 : 0,
        }}
      >
        {parts.map((p) =>
          p.v > 0 ? (
            <div
              key={p.label}
              className={p.color}
              style={{ height: `${(p.v / total) * 100}%` }}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

// ─── Period chart: cost vs invoiced per bucket ─────────────────────────────
function PeriodChart({ buckets, selected, onSelect }) {
  const max = Math.max(
    1,
    ...buckets.map((b) => Math.max(b.costIncurred, b.invoiced)),
  );
  const minWidth = buckets.length * 56;
  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth }}>
        <div className="relative h-56">
          {[0.25, 0.5, 0.75, 1].map((t) => (
            <div
              key={t}
              className="absolute left-0 right-0 border-t border-dashed border-gray-700/70"
              style={{ bottom: `${t * 100}%` }}
            >
              <span className="absolute -top-2 left-0 text-[10px] text-gray-500 bg-gray-800 pr-1 tabular-nums">
                {fmtCompact(max * t)}
              </span>
            </div>
          ))}
          <div className="absolute inset-0 pl-12 flex items-end gap-2">
            {buckets.map((b) => {
              const active = b.key === selected;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => onSelect(b.key)}
                  aria-pressed={active}
                  aria-label={`${b.label}: cost ${fmt(b.costIncurred)}, invoiced ${fmt(b.invoiced)}`}
                  className={cls(
                    "flex-1 h-full flex items-end justify-center gap-1 rounded-t-md pt-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400",
                    active
                      ? "bg-orange-500/10 ring-1 ring-orange-500/40"
                      : "hover:bg-gray-700/30",
                  )}
                >
                  <StackBar series={COST_SERIES} m={b} max={max} title="Cost" />
                  <StackBar
                    series={BILL_SERIES}
                    m={b}
                    max={max}
                    title="Invoiced"
                  />
                </button>
              );
            })}
          </div>
        </div>
        <div className="pl-12 flex gap-2 mt-2">
          {buckets.map((b) => (
            <div
              key={b.key}
              className={cls(
                "flex-1 text-center text-[11px] tabular-nums truncate",
                b.key === selected
                  ? "text-orange-300 font-semibold"
                  : "text-gray-500",
              )}
            >
              {b.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Horizontal composition bar with rows ──────────────────────────────────
function Composition({ items, footer }) {
  const total = items.reduce((s, i) => s + Math.max(0, i.value), 0);
  return (
    <div>
      <div className="h-2.5 w-full bg-gray-700 rounded-full overflow-hidden flex">
        {total > 0 &&
          items.map((i) =>
            i.value > 0 ? (
              <div
                key={i.label}
                className={i.color}
                style={{ width: `${(i.value / total) * 100}%` }}
                title={`${i.label}: ${fmt(i.value)}`}
              />
            ) : null,
          )}
      </div>
      <dl className="mt-4 space-y-2">
        {items.map((i) => (
          <div
            key={i.label}
            className="flex items-center justify-between gap-3 text-sm"
          >
            <dt className="flex items-center gap-2 text-gray-300 min-w-0">
              <span className={cls("w-2 h-2 rounded-sm shrink-0", i.color)} />
              <span className="truncate">{i.label}</span>
              {i.note && (
                <span className="text-[11px] text-gray-500 truncate">
                  {i.note}
                </span>
              )}
            </dt>
            <dd className="tabular-nums text-gray-100 shrink-0">
              {fmt(i.value)}
              <span className="ml-2 text-[11px] text-gray-500 inline-block w-10 text-right">
                {total > 0
                  ? `${((Math.max(0, i.value) / total) * 100).toFixed(0)}%`
                  : ""}
              </span>
            </dd>
          </div>
        ))}
      </dl>
      {footer && (
        <div className="mt-4 pt-3 border-t border-gray-700 space-y-1.5">
          {footer}
        </div>
      )}
    </div>
  );
}

function FooterLine({ label, value, tone = "text-gray-100", strong }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={strong ? "text-gray-200 font-medium" : "text-gray-400"}>
        {label}
      </span>
      <span className={cls("tabular-nums", tone, strong && "font-semibold")}>
        {value}
      </span>
    </div>
  );
}

function Panel({ title, aside, children, className }) {
  return (
    <section
      className={cls(
        "bg-gray-800 border border-gray-700 rounded-xl p-5",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-sm font-semibold text-white">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function DetailGrid({ r }) {
  const groups = [
    {
      title: "Contract",
      rows: [
        ["Original contract", fmt(r.originalContractValue)],
        ["Approved COs to date", fmt(r.coToDate)],
        ["Current contract", fmt(r.contractValue)],
        [
          "Billed to date",
          `${fmt(r.billedToDate)} (${fmtPct(r.billedToDatePct)})`,
        ],
      ],
    },
    {
      title: "Change orders in period",
      rows: [
        ["Approved (billable)", `${fmt(r.coValue)} · ${r.coCount}`],
        ["Non-billable", fmt(r.coNonBillable)],
        ["CO cost", fmt(r.coCost)],
      ],
    },
    {
      title: "Labour",
      rows: [
        ["Piecework approved", fmt(r.pwApproved)],
        ["Piecework burden", fmt(r.pwBurden)],
        ["Piecework qty", fmtNum(r.pwQty, 2)],
        ["Piecework pending", fmt(r.pwPending)],
        ["Hourly approved", `${fmt(r.tsApproved)} · ${fmtNum(r.tsHours, 1)} h`],
        ["Hourly burden + WSIB", fmt(r.tsBurden + r.tsWsib)],
        ["Hourly pending", fmt(r.tsPending)],
      ],
    },
    {
      title: "Materials & subs",
      rows: [
        ["Ordered", fmt(r.matOrdered)],
        ["Delivered", fmt(r.matDelivered)],
        ["Surcharges", fmt(r.matSurcharge)],
        ["Sub bills processed", fmt(r.subProcessed)],
        ["Sub bills pending", fmt(r.subPending)],
        ["Sub agreements completed", fmt(r.saActual)],
        ["Sub agreements issued", fmt(r.saPending)],
        ["Payment processing fees", fmt(r.payFees)],
      ],
    },
    {
      title: "Invoicing in period",
      rows: [
        ["Progress (gross)", fmt(r.invProgressGross)],
        ["Holdback deducted", fmt(r.holdbackDeducted)],
        ["CO invoices (net)", fmt(r.invStandaloneNet)],
        ["Holdback released", fmt(r.invHoldbackRelease)],
        ["Invoice totals incl. tax", fmt(r.invTotal)],
        ["Paid", fmt(r.paid)],
      ],
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 px-4 py-4 bg-gray-900/60">
      {groups.map((g) => (
        <div key={g.title}>
          <div className="text-xs font-medium text-orange-300 mb-2">
            {g.title}
          </div>
          <dl className="space-y-1">
            {g.rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 text-xs">
                <dt className="text-gray-400">{k}</dt>
                <dd className="text-gray-200 tabular-nums text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════════════════════
export default function FinanceDashboard3() {
  const token = localStorage.getItem("token");

  const [rangeState, setRangeState] = useState(INITIAL_RANGE);
  const [granularity, setGranularity] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedBucket, setSelectedBucket] = useState(null);
  const [view, setView] = useState("overview");
  const [search, setSearch] = useState("");
  const [hideInactive, setHideInactive] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);
  const [sort, setSort] = useState({ key: "invoiced", dir: "desc" });
  const [expanded, setExpanded] = useState(() => new Set());
  const [coOpen, setCoOpen] = useState(false);
  const [page, setPage] = useState("performance");

  const range = useMemo(() => resolveRange(rangeState), [rangeState]);
  const startMs = range?.start?.getTime();
  const endMs = range?.end?.getTime();

  // Year view reads better by quarter; short ranges by month
  useEffect(() => {
    if (rangeState.mode === "year") setGranularity("quarter");
    if (rangeState.mode === "month" || rangeState.mode === "quarter")
      setGranularity("month");
  }, [rangeState.mode]);

  useEffect(() => {
    if (!startMs || !endMs) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/finance3/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          from: new Date(startMs).toISOString(),
          to: new Date(endMs).toISOString(),
          granularity,
        },
      })
      .then((res) => {
        if (cancelled) return;
        if (res.data.success) {
          setData(res.data);
          const withActivity = [...(res.data.buckets || [])]
            .reverse()
            .find((b) => b.costIncurred || b.invoiced || b.coValue);
          setSelectedBucket(
            withActivity?.key || res.data.buckets?.[0]?.key || null,
          );
        } else setError(res.data.message || "Finance data didn't load.");
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startMs, endMs, granularity, token]);

  const t = data?.totals;
  const buckets = data?.buckets || [];
  const bucket = buckets.find((b) => b.key === selectedBucket);

  const statuses = useMemo(
    () =>
      [
        ...new Set((data?.projects || []).map((p) => p.status).filter(Boolean)),
      ].sort(),
    [data],
  );

  const columns = COLUMNS[view];

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = (data?.projects || []).filter((p) => {
      if (hideInactive && !p.hasActivity) return false;
      if (statusFilter.length && !statusFilter.includes(p.status)) return false;
      if (!q) return true;
      return (
        String(p.projectName || "")
          .toLowerCase()
          .includes(q) ||
        String(p.projectNumber || "")
          .toLowerCase()
          .includes(q)
      );
    });
    const col =
      Object.values(COLUMNS)
        .flat()
        .find((c) => c.key === sort.key) || null;
    list = [...list].sort((a, b) => {
      if (sort.key === "projectName") {
        const r = String(a.projectNumber || a.projectName).localeCompare(
          String(b.projectNumber || b.projectName),
        );
        return sort.dir === "asc" ? r : -r;
      }
      const av = col ? (col.get(a) ?? -Infinity) : 0;
      const bv = col ? (col.get(b) ?? -Infinity) : 0;
      return sort.dir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [data, search, hideInactive, statusFilter, sort]);

  const footer = useMemo(() => {
    const f = {};
    rows.forEach((r) => {
      Object.entries(r).forEach(([k, v]) => {
        if (typeof v === "number" && isFinite(v)) f[k] = (f[k] || 0) + v;
      });
    });
    f.marginPct = f.invoiced > 0 ? (f.margin / f.invoiced) * 100 : null;
    f.billedToDatePct =
      f.contractValue > 0 ? (f.billedToDate / f.contractValue) * 100 : null;
    return f;
  }, [rows]);

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" },
    );

  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const exportCsv = () => {
    const allCols = [];
    const seen = new Set();
    Object.values(COLUMNS)
      .flat()
      .forEach((c) => {
        if (!seen.has(c.key)) {
          seen.add(c.key);
          allCols.push(c);
        }
      });
    const header = [
      "Project #",
      "Project",
      "Status",
      "Contract value",
      "Billed to date",
      ...allCols.map((c) => c.label),
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((r) =>
      [
        r.projectNumber,
        r.projectName,
        r.status,
        r.contractValue.toFixed(2),
        r.billedToDate.toFixed(2),
        ...allCols.map((c) => {
          const v = c.get(r);
          return v == null ? "" : Number(v).toFixed(2);
        }),
      ]
        .map(esc)
        .join(","),
    );
    const blob = new Blob([[header.map(esc).join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finance-${(range?.label || "range").replace(/[^a-z0-9]+/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (c, r) => {
    const v = c.get(r);
    const sub = c.sub ? c.sub(r) : null;
    const tone = c.tone ? c.tone(r) : "text-gray-200";
    const empty = v == null || (c.kind !== "pct" && Math.abs(v) < 0.005);
    return (
      <>
        <div className={cls("tabular-nums", empty ? "text-gray-600" : tone)}>
          {empty ? "—" : c.kind === "pct" ? fmtPct(v) : fmt(v)}
        </div>
        {sub && !empty && (
          <div className="text-[10px] text-gray-500">{sub}</div>
        )}
      </>
    );
  };

  return (
    <div className="bg-gray-900 rounded-xl p-1 min-h-full mx-4 mt-2">
      <div className="max-w-full mx-auto px-4 py-6 space-y-6">
        {/* ── Header + range controls ── */}
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Finance
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {page === "performance"
                ? "Costs, change orders and invoicing across every V3 project"
                : "Receivables, payables and overhead for review and reporting"}
            </p>
          </div>

          {page === "performance" && (
            <div className="flex flex-col gap-2 xl:items-end">
              <div className="flex flex-wrap items-center gap-2">
                <Segmented
                  size="sm"
                  value={rangeState.mode}
                  onChange={(mode) => setRangeState((r) => ({ ...r, mode }))}
                  options={[
                    { key: "quarter", label: "Quarter" },
                    { key: "month", label: "Month" },
                    { key: "year", label: "Year" },
                    { key: "custom", label: "Custom" },
                  ]}
                />

                {(rangeState.mode === "quarter" ||
                  rangeState.mode === "year") && (
                  <select
                    value={rangeState.year}
                    onChange={(e) =>
                      setRangeState((r) => ({
                        ...r,
                        year: Number(e.target.value),
                      }))
                    }
                    className="bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    aria-label="Year"
                  >
                    {YEARS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {rangeState.mode === "quarter" && (
                  <Segmented
                    size="sm"
                    value={String(rangeState.quarter)}
                    onChange={(q) =>
                      setRangeState((r) => ({ ...r, quarter: Number(q) }))
                    }
                    options={[1, 2, 3, 4].map((q) => ({
                      key: String(q),
                      label: `Q${q}`,
                    }))}
                  />
                )}

                {rangeState.mode === "month" && (
                  <input
                    type="month"
                    value={rangeState.month}
                    onChange={(e) =>
                      setRangeState((r) => ({ ...r, month: e.target.value }))
                    }
                    className="bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-lg px-2.5 py-1.5 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-orange-400"
                    aria-label="Month"
                  />
                )}

                {rangeState.mode === "custom" && (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="date"
                      value={rangeState.from}
                      onChange={(e) =>
                        setRangeState((r) => ({ ...r, from: e.target.value }))
                      }
                      className="bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-lg px-2.5 py-1.5 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-orange-400"
                      aria-label="From"
                    />
                    <span className="text-gray-500 text-sm">to</span>
                    <input
                      type="date"
                      value={rangeState.to}
                      onChange={(e) =>
                        setRangeState((r) => ({ ...r, to: e.target.value }))
                      }
                      className="bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-lg px-2.5 py-1.5 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-orange-400"
                      aria-label="To"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setRangeState((r) => p.apply(r))}
                    className="px-2.5 py-1 text-xs rounded-md text-gray-400 border border-gray-700 hover:text-white hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Page tabs ── */}
        <div className="flex gap-6 border-b border-gray-700" role="tablist">
          {[
            { key: "performance", label: "Project performance" },
            { key: "accounting", label: "Receivables, payables & overhead" },
          ].map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={page === p.key}
              onClick={() => setPage(p.key)}
              className={cls(
                "-mb-px pb-2.5 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:text-white",
                page === p.key
                  ? "border-orange-500 text-white"
                  : "border-transparent text-gray-400 hover:text-gray-200",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        {page === "accounting" && <AccountingReports3 />}

        {page === "performance" && !range && (
          <div className="bg-yellow-900/20 border border-yellow-700/40 rounded-lg px-4 py-3 text-sm text-yellow-300">
            Pick a start date that's on or before the end date.
          </div>
        )}

        {page === "performance" && error && (
          <div className="bg-red-950 border border-red-700/40 rounded-lg px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {page === "performance" && !data && loading && (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            Loading finance data…
          </div>
        )}

        {page === "performance" && data && t && (
          <div
            className={cls(
              "space-y-6 transition-opacity",
              loading && "opacity-60",
            )}
          >
            {/* ── Period headline ── */}
            <section className="bg-gray-800 border border-gray-600 rounded-xl overflow-hidden">
              <div className="px-6 pt-5 pb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-700">
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                    {range?.label}
                  </span>
                  {range && rangeState.mode !== "custom" && (
                    <span className="text-sm text-gray-500">
                      {fmtDay(range.start)} – {fmtDay(range.end)}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  {t.activeProjectCount} of {t.projectCount} projects with
                  activity
                  {loading && (
                    <span className="ml-2 text-orange-300">Refreshing…</span>
                  )}
                </span>
              </div>
              <div className="px-6 py-5 grid grid-cols-2 lg:grid-cols-5 gap-6">
                <Figure
                  label="Invoiced (net of holdback)"
                  value={fmt(t.invoiced)}
                  sub={`${fmt(t.paid)} paid · ${fmt(t.ar)} outstanding`}
                  tone="text-green-400"
                />
                <Figure
                  label="Cost incurred"
                  value={fmt(t.costIncurred)}
                  sub={`${fmt(t.committed)} committed`}
                />
                <Figure
                  label="Gross margin"
                  value={fmt(t.margin)}
                  sub={
                    t.marginPct == null
                      ? "No invoicing in period"
                      : `${fmtPct(t.marginPct)} of invoiced`
                  }
                  tone={t.margin < 0 ? "text-red-400" : "text-orange-300"}
                />
                <Figure
                  label="Change orders approved"
                  value={fmt(t.coValue)}
                  sub={`${t.coCount} billable${t.coNonBillable ? ` · ${fmt(t.coNonBillable)} non-billable` : ""}`}
                  tone="text-purple-300"
                />
                <Figure
                  label="Overhead"
                  value={
                    data.overhead.connected
                      ? fmt(t.overhead + t.labourBurden)
                      : fmt(t.labourBurden)
                  }
                  sub={
                    data.overhead.connected
                      ? `${fmt(t.overhead)} company · ${fmt(t.labourBurden)} labour burden`
                      : "Labour burden only"
                  }
                  tone="text-gray-200"
                />
              </div>
            </section>

            {/* ── Period chart ── */}
            <Panel
              title="Cost vs invoiced by period"
              aside={
                <Segmented
                  size="sm"
                  value={granularity}
                  onChange={setGranularity}
                  options={[
                    { key: "month", label: "Monthly" },
                    { key: "quarter", label: "Quarterly" },
                  ]}
                />
              }
            >
              {buckets.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  No periods in this range.
                </div>
              ) : (
                <>
                  <PeriodChart
                    buckets={buckets}
                    selected={selectedBucket}
                    onSelect={setSelectedBucket}
                  />
                  <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-6">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 w-14">
                          Left bar
                        </span>
                        <Legend series={COST_SERIES} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 w-14">
                          Right bar
                        </span>
                        <Legend series={BILL_SERIES} />
                      </div>
                    </div>
                  </div>

                  {bucket && (
                    <div className="mt-5 rounded-lg border border-orange-500/30 bg-gray-900/60 px-4 py-3 grid grid-cols-2 md:grid-cols-6 gap-4">
                      <div className="col-span-2 md:col-span-1">
                        <div className="text-xs text-gray-400">
                          Selected period
                        </div>
                        <div className="text-base font-semibold text-orange-300 mt-0.5">
                          {bucket.label}
                        </div>
                      </div>
                      {[
                        [
                          "Cost incurred",
                          fmt(bucket.costIncurred),
                          "text-gray-100",
                        ],
                        ["Invoiced", fmt(bucket.invoiced), "text-green-400"],
                        [
                          "Margin",
                          `${fmt(bucket.margin)}${bucket.marginPct != null ? ` · ${fmtPct(bucket.marginPct)}` : ""}`,
                          bucket.margin < 0
                            ? "text-red-400"
                            : "text-orange-300",
                        ],
                        [
                          "COs approved",
                          `${fmt(bucket.coValue)} · ${bucket.coCount}`,
                          "text-purple-300",
                        ],
                        [
                          "Labour burden",
                          fmt(bucket.labourBurden),
                          "text-gray-200",
                        ],
                      ].map(([k, v, tone]) => (
                        <div key={k}>
                          <div className="text-xs text-gray-400">{k}</div>
                          <div
                            className={cls(
                              "text-sm font-semibold tabular-nums mt-0.5",
                              tone,
                            )}
                          >
                            {v}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Panel>

            {/* ── Breakdowns ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Panel title="Where the cost went">
                <Composition
                  items={[
                    {
                      label: "Materials delivered",
                      value: t.matDelivered,
                      color: "bg-orange-400",
                    },
                    {
                      label: "Material surcharges",
                      value: t.matSurcharge,
                      color: "bg-orange-200",
                    },
                    {
                      label: "Piecework logs",
                      value: t.pwApproved,
                      color: "bg-purple-400",
                      note: `${fmtNum(t.pwLogs)} logs`,
                    },
                    {
                      label: "Hourly timesheets",
                      value: t.tsApproved,
                      color: "bg-sky-400",
                      note: `${fmtNum(t.tsHours, 1)} h`,
                    },
                    {
                      label: "Subcontractor bills",
                      value: t.subProcessed,
                      color: "bg-teal-400",
                    },
                    {
                      label: "Sub agreements",
                      value: t.saActual,
                      color: "bg-rose-400",
                      note: `${fmtNum(t.saCount)} completed`,
                    },
                    {
                      label: "Payment processing fees",
                      value: t.payFees,
                      color: "bg-amber-300",
                    },
                  ]}
                />
              </Panel>

              <Panel title="What was invoiced">
                <Composition
                  items={[
                    {
                      label: "Progress draws",
                      value: t.invProgressNet,
                      color: "bg-emerald-500",
                    },
                    {
                      label: "Standalone CO invoices",
                      value: t.invStandaloneNet,
                      color: "bg-emerald-300",
                    },
                    {
                      label: "Holdback released",
                      value: t.invHoldbackRelease,
                      color: "bg-lime-300",
                    },
                  ]}
                  footer={
                    <>
                      <FooterLine
                        label="Invoiced (net)"
                        value={fmt(t.invoiced)}
                        strong
                        tone="text-green-400"
                      />
                      <FooterLine
                        label="Holdback retained this period"
                        value={fmt(t.holdbackDeducted + t.standaloneHoldback)}
                        tone="text-purple-300"
                      />
                      <FooterLine
                        label="Paid against these invoices"
                        value={fmt(t.paid)}
                        tone="text-blue-400"
                      />
                      <FooterLine
                        label="Still receivable"
                        value={fmt(t.ar)}
                        tone="text-orange-300"
                      />
                      <FooterLine
                        label="Contract billed to date"
                        value={`${fmt(t.billedToDate)} of ${fmt(t.contractValue)} (${fmtPct(t.billedToDatePct)})`}
                      />
                    </>
                  }
                />
              </Panel>

              <Panel title="Overhead and burden">
                <Composition
                  items={[
                    {
                      label: "Piecework burden",
                      value: t.pwBurden,
                      color: "bg-purple-300",
                    },
                    {
                      label: "Hourly burden",
                      value: t.tsBurden,
                      color: "bg-sky-300",
                    },
                    { label: "WSIB", value: t.tsWsib, color: "bg-sky-200" },
                    ...(data.overhead.connected
                      ? data.overhead.byCategory.slice(0, 5).map((c, i) => ({
                          label: c.category,
                          value: c.amount,
                          color: [
                            "bg-gray-300",
                            "bg-gray-400",
                            "bg-gray-500",
                            "bg-stone-400",
                            "bg-stone-500",
                          ][i],
                        }))
                      : []),
                  ]}
                  footer={
                    <>
                      <FooterLine
                        label="Gross margin"
                        value={fmt(t.margin)}
                        tone={t.margin < 0 ? "text-red-400" : "text-gray-100"}
                      />
                      {data.overhead.connected ? (
                        <>
                          <FooterLine
                            label="Company overhead"
                            value={`−${fmt(t.overhead)}`}
                            tone="text-gray-300"
                          />
                          <FooterLine
                            label="Net after overhead"
                            value={fmt(t.netAfterOverhead)}
                            strong
                            tone={
                              t.netAfterOverhead < 0
                                ? "text-red-400"
                                : "text-orange-300"
                            }
                          />
                        </>
                      ) : (
                        <p className="text-xs text-gray-500">
                          Company overhead isn't connected yet. Labour burden is
                          already included in cost incurred.
                        </p>
                      )}
                    </>
                  }
                />
              </Panel>
            </div>

            {/* ── Projects table ── */}
            <section className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <h2 className="text-sm font-semibold text-white">Projects</h2>
                  <span className="text-xs text-gray-500">
                    {rows.length} shown
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Segmented
                    size="sm"
                    value={view}
                    onChange={setView}
                    options={VIEWS}
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name or number"
                    className="bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-lg px-3 py-1.5 w-52 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={hideInactive}
                      onChange={(e) => setHideInactive(e.target.checked)}
                      className="accent-orange-500"
                    />
                    Hide projects with no activity
                  </label>
                  <button
                    type="button"
                    onClick={exportCsv}
                    disabled={rows.length === 0}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              {statuses.length > 1 && (
                <div className="px-5 py-2.5 border-b border-gray-700 flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-gray-500 mr-1">Status</span>
                  {statuses.map((s) => {
                    const on = statusFilter.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() =>
                          setStatusFilter((prev) =>
                            on ? prev.filter((x) => x !== s) : [...prev, s],
                          )
                        }
                        className={cls(
                          "px-2 py-0.5 rounded-md text-xs border capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400",
                          on
                            ? "bg-orange-500/20 border-orange-500/60 text-orange-200"
                            : "border-gray-700 text-gray-400 hover:text-white",
                        )}
                      >
                        {s}
                      </button>
                    );
                  })}
                  {statusFilter.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setStatusFilter([])}
                      className="text-xs text-gray-500 hover:text-white ml-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/80">
                    <tr>
                      <th className="sticky left-0 z-10 bg-gray-900 px-4 py-2.5 text-left">
                        <button
                          type="button"
                          onClick={() => toggleSort("projectName")}
                          className="text-xs font-medium text-gray-400 hover:text-white"
                        >
                          Project
                          {sort.key === "projectName"
                            ? sort.dir === "asc"
                              ? " ↑"
                              : " ↓"
                            : ""}
                        </button>
                      </th>
                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className="px-3 py-2.5 text-right whitespace-nowrap"
                        >
                          <button
                            type="button"
                            onClick={() => toggleSort(c.key)}
                            className={cls(
                              "text-xs font-medium hover:text-white",
                              sort.key === c.key
                                ? "text-orange-300"
                                : "text-gray-400",
                            )}
                          >
                            {c.label}
                            {sort.key === c.key
                              ? sort.dir === "asc"
                                ? " ↑"
                                : " ↓"
                              : ""}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/60">
                    {rows.map((r) => {
                      const id = String(r._id);
                      const open = expanded.has(id);
                      return (
                        <Fragment key={id}>
                          <tr
                            onClick={() => toggleExpanded(id)}
                            className={cls(
                              "cursor-pointer",
                              open ? "bg-gray-700/30" : "hover:bg-gray-700/20",
                            )}
                          >
                            <td
                              className={cls(
                                "sticky left-0 z-10 px-4 py-2.5",
                                open ? "bg-gray-700" : "bg-gray-800",
                              )}
                            >
                              <div className="flex items-center gap-2 min-w-[220px]">
                                <svg
                                  className={cls(
                                    "w-3.5 h-3.5 text-gray-500 shrink-0 transition-transform",
                                    open && "rotate-90 text-orange-300",
                                  )}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <div className="min-w-0">
                                  <div
                                    className="text-gray-100 font-medium truncate max-w-[260px]"
                                    title={r.projectName}
                                  >
                                    {r.projectName}
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    #{r.projectNumber || "—"}
                                    {r.status && (
                                      <span className="ml-2 capitalize">
                                        {r.status}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {columns.map((c) => (
                              <td
                                key={c.key}
                                className="px-3 py-2.5 text-right whitespace-nowrap"
                              >
                                {renderCell(c, r)}
                              </td>
                            ))}
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={columns.length + 1} className="p-0">
                                <DetailGrid r={r} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={columns.length + 1}
                          className="px-4 py-12 text-center text-sm text-gray-500"
                        >
                          {hideInactive
                            ? "No project had costs, change orders or invoices in this period. Turn off “Hide projects with no activity” to see all V3 projects."
                            : "No V3 projects match these filters."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot className="bg-gray-900/80 border-t border-gray-600">
                      <tr>
                        <td className="sticky left-0 z-10 bg-gray-900 px-4 py-3 text-sm font-semibold text-white">
                          Total · {rows.length} projects
                        </td>
                        {columns.map((c) => (
                          <td
                            key={c.key}
                            className="px-3 py-3 text-right whitespace-nowrap font-semibold"
                          >
                            {renderCell({ ...c, sub: null }, footer)}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </section>

            {/* ── Change orders approved in period ── */}
            {data.sources.changeOrders && (
              <section className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCoOpen((v) => !v)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-700/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400"
                  aria-expanded={coOpen}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-sm font-semibold text-white">
                      Change orders approved in this period
                    </h2>
                    <span className="text-xs text-gray-500">
                      {data.changeOrders.length} ·{" "}
                      {fmt(
                        data.changeOrders.reduce(
                          (s, c) =>
                            s + (c.billingMode === "nonBillable" ? 0 : c.value),
                          0,
                        ),
                      )}{" "}
                      billable
                    </span>
                  </div>
                  <svg
                    className={cls(
                      "w-4 h-4 text-gray-400 transition-transform",
                      coOpen && "rotate-180",
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {coOpen && (
                  <div className="overflow-x-auto border-t border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-900/70">
                        <tr>
                          {[
                            "Approved",
                            "CO",
                            "Project",
                            "Title",
                            "Billing",
                            "Customer price",
                            "Cost",
                          ].map((h, i) => (
                            <th
                              key={h}
                              className={cls(
                                "px-4 py-2 text-xs font-medium text-gray-400 whitespace-nowrap",
                                i >= 5 ? "text-right" : "text-left",
                              )}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/60">
                        {data.changeOrders.map((co) => (
                          <tr key={co._id} className="hover:bg-gray-700/20">
                            <td className="px-4 py-2 text-gray-400 whitespace-nowrap tabular-nums">
                              {co.approvedOn
                                ? fmtDay(new Date(co.approvedOn))
                                : "—"}
                            </td>
                            <td className="px-4 py-2 text-gray-300 whitespace-nowrap">
                              CO-
                              {String(co.changeOrderNumber || "").padStart(
                                3,
                                "0",
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-300 whitespace-nowrap">
                              <span className="text-gray-500 mr-1">
                                #{co.projectNumber}
                              </span>
                              {co.projectName}
                            </td>
                            <td
                              className="px-4 py-2 text-gray-200 max-w-[320px] truncate"
                              title={co.title}
                            >
                              {co.title}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {co.billingMode === "separate" ? (
                                <span className="text-purple-300">
                                  Separate invoice
                                </span>
                              ) : co.billingMode === "nonBillable" ? (
                                <span className="text-gray-500">
                                  Non-billable
                                </span>
                              ) : (
                                <span className="text-green-400">On draws</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-100">
                              {fmt(co.value)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                              {co.cost ? fmt(co.cost) : "—"}
                            </td>
                          </tr>
                        ))}
                        {data.changeOrders.length === 0 && (
                          <tr>
                            <td
                              colSpan={7}
                              className="px-4 py-8 text-center text-sm text-gray-500"
                            >
                              No change orders were approved in this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
